import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { AppShell } from '@/components/layout/AppShell';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/button';
import { Column, Card, Modal, Input, DeleteConfirmDialog } from '@/components/ui';
import { ResponsiveTable as DataTable } from '@/components/ui/responsive-table';
import { useDataTableFilters, DataTableFilter, createColumnConfigHelper, applyFilters } from '@/components/data-table-filter';
import { edgeDeviceService } from '../../services/edgeDeviceService';
import { deploymentService } from '../../services/deploymentService';
import { CentralEdgeDevice } from '@simcheck/shared-types';
import {
  Key, Server, CheckCircle, XCircle, Copy, RefreshCw, Loader2,
  Shield, Clock, Terminal, Plus, Hash, ShieldCheck, ShieldX, ShieldAlert,
  AlertTriangle, RotateCcw
} from 'lucide-react';
import { auth } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useUserTimezone } from '@/hooks/useDateFormat';
import { INSTALL_PHASES, getPhaseIndex, formatElapsed } from '@/lib/installPhases';
import { CENTRAL_API_URL } from '@/config/env';

async function getCentralAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
}

interface ProvisioningToken {
  id: string;
  token: string;
  usageType: 'single' | 'limited' | 'unlimited';
  maxUses?: number;
  useCount: number;
  status: 'active' | 'exhausted' | 'expired' | 'revoked';
  // Super-admin-minted bounded token whose devices skip portal approval (#264).
  // Absent on tokens minted before the feature existed.
  autoApprove?: boolean;
  createdAt: any;
  createdBy: string;
  expiresAt: any;
  enrolledDevices: string[];
  // Derived after fetch for DataTable sorting (see loadData)
  devicesCount?: number;
  expiresMs?: number;
}

interface DeviceAuthInfo {
  id: string;
  name: string;
  deviceId: string;
  ownerOrgId: string | null;
  registrationStatus: 'pending' | 'approved' | 'rejected';
  deviceRevoked: boolean;
  deviceSecretDelivered: boolean;
  hasSecretHash: boolean;
  approvedAt: Date | null;
  revokedAt: Date | null;
  // Revoke parks the device's credentials until this time; before it, a
  // one-click Restore brings the device back without an on-site re-install (#302).
  restorableUntil: Date | null;
  lastSeen: Date | null;
  status: string;
  // Mid-install telemetry (issue #106). Cleared server-side when the updater
  // reports 'healthy' / 'failed' / 'rolled_back'.
  installPhase: string | null;
  installPhaseStartedAt: number | null;
}

function parseTimestamp(ts: any): Date | null {
  if (!ts) return null;
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (ts._seconds) return new Date(ts._seconds * 1000);
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(d: Date | null, timeZone?: string): string {
  if (!d) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone });
}

function formatRelative(d: Date | null): string {
  if (!d) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(d);
}

// Install progress badge for the Auth tab row. Compact two-line display.
// The same phase data drives the bigger banner on /edges/:id (see
// EdgeDeviceDetail.tsx). Shared phase constants in @/lib/installPhases.
function InstallProgressBadge({ phase, startedAt }: { phase: string; startedAt?: number }) {
  // Local timer ticks once a second so elapsed time keeps moving even while
  // the device-list cache (5 s on central-server + 15 s on the client)
  // hasn't refreshed. This is the visual signal that "still in motion".
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const idx = getPhaseIndex(phase);
  if (idx < 0) {
    return (
      <Badge variant="outline" className="text-xs">
        <Loader2 className="w-3 h-3 mr-1 animate-spin" /> {phase}
      </Badge>
    );
  }
  const entry = INSTALL_PHASES[idx];
  const elapsedMs = startedAt ? now - startedAt : 0;
  const isAwaitingReboot = entry.key === 'awaiting_reboot';
  return (
    <div className="flex flex-col gap-0.5 min-w-[200px]">
      <div className="flex items-center gap-1.5 text-xs">
        <Loader2 className="w-3 h-3 animate-spin text-accent-light-blue" />
        <span className="text-foreground">
          Step {idx + 1}/{INSTALL_PHASES.length} — {entry.label}
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground pl-[18px]">
        {startedAt ? `${formatElapsed(elapsedMs)} elapsed` : 'just started'}
        {isAwaitingReboot && ' • press Enter in the SSH terminal to reboot now'}
      </div>
    </div>
  );
}

// ── Generate Token Modal ──────────────────────────────────────────────

function GenerateTokenModal({ onClose, onGenerated }: { onClose: () => void; onGenerated: () => void }) {
  // Tenancy: bind the new token to the caller's active org. Super-admin
  // browsing without an active org context (i.e. the legacy /admin/device-
  // provisioning view) gets `null` here, which central-server interprets as
  // "shared token" — only super-admin can mint that. For any tenant or for
  // a super-admin viewing-as-org, the token is bound to that org and the
  // device gets stamped with that ownerOrgId at bootstrap.
  const { activeOrganizationId, isSuperAdmin } = useAuth();
  const [usageType, setUsageType] = useState<'single' | 'limited' | 'unlimited'>('unlimited');
  const [maxUses, setMaxUses] = useState(20);
  const [expiryDays, setExpiryDays] = useState(30);
  const [generating, setGenerating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Super-admin can explicitly opt to mint a SHARED token (no org binding)
  // when there's no active org context. Tenants can never opt to shared.
  const [mintShared, setMintShared] = useState(false);
  // Auto-approve (#264) — super-admin only, and only on a bounded token.
  // central-server enforces both; the UI just mirrors the rules so the
  // operator doesn't discover them via a 400.
  const [autoApprove, setAutoApprove] = useState(false);

  const targetOrgId = mintShared ? null : (activeOrganizationId ?? null);
  const autoApproveAllowed = isSuperAdmin && usageType !== 'unlimited';
  // An unlimited token can never auto-approve; flipping usage type back to
  // unlimited silently drops the flag rather than sending a doomed request.
  const effectiveAutoApprove = autoApprove && autoApproveAllowed;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const headers = await getCentralAuthHeaders();
      const body: Record<string, unknown> = {
        usageType,
        maxUses: usageType === 'limited' ? maxUses : undefined,
        expiryDays,
      };
      // Only include organizationId when we actually have one. Omitting the
      // key tells central-server "shared token", which it gates on
      // super-admin via canMintTokenForOrg.
      if (targetOrgId) body.organizationId = targetOrgId;
      if (effectiveAutoApprove) body.autoApprove = true;
      const res = await fetch(`${CENTRAL_API_URL}/certificates/tokens`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`Failed (${res.status}): ${errBody || res.statusText}`);
      }
      const data = await res.json();
      setGeneratedToken(data.token);
      onGenerated();
    } catch (e: any) {
      console.error('Failed to generate token:', e);
      setError(e?.message || 'Failed to generate token');
    }
    setGenerating(false);
  };

  const curlCommand = generatedToken
    ? `curl -fsSL "${CENTRAL_API_URL.replace('/api', '')}/api/bootstrap/install?token=${generatedToken}" | sudo bash`
    : '';

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-lg">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Key className="w-5 h-5" /> Generate Provisioning Token
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl">&times;</button>
        </div>

        <div className="p-6 space-y-4">
          {!generatedToken ? (
            <>
              {/* Usage Type */}
              <div>
                <label className="text-sm font-medium text-foreground">Usage Type</label>
                <div className="flex gap-2 mt-1">
                  {(['single', 'limited', 'unlimited'] as const).map(t => (
                    <button
                      key={t}
                      onClick={() => setUsageType(t)}
                      className={`px-3 py-1.5 rounded text-xs font-medium border capitalize ${
                        usageType === t
                          ? 'border-accent-blue bg-accent-blue/10 text-accent-light-blue'
                          : 'border-border text-muted-foreground'
                      }`}
                    >{t === 'single' ? 'Single use' : t === 'limited' ? 'Limited uses' : 'Unlimited'}</button>
                  ))}
                </div>
              </div>

              {usageType === 'limited' && (
                <div>
                  <label className="text-sm font-medium text-foreground">Max Devices</label>
                  <input
                    type="number"
                    value={maxUses}
                    onChange={e => setMaxUses(parseInt(e.target.value) || 1)}
                    min={1}
                    className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-foreground">Expires in (days)</label>
                <input
                  type="number"
                  value={expiryDays}
                  onChange={e => setExpiryDays(parseInt(e.target.value) || 1)}
                  min={1}
                  max={365}
                  className="mt-1 w-full px-3 py-2 bg-muted border border-border rounded text-sm text-foreground"
                />
              </div>

              {/* Org binding — devices bootstrapped with this token inherit
                  this ownership. Tenants can only mint for their active org;
                  super-admin can mint shared (no org binding). */}
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
                <div className="font-medium text-foreground">Token will bind devices to:</div>
                <div className="mt-1 text-muted-foreground">
                  {targetOrgId
                    ? <>Org <code className="text-foreground">{targetOrgId}</code></>
                    : <>Shared fleet <span className="text-status-warning">(super-admin only)</span></>}
                </div>
                {isSuperAdmin && activeOrganizationId && (
                  <label className="mt-2 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mintShared}
                      onChange={e => setMintShared(e.target.checked)}
                      className="h-3.5 w-3.5"
                    />
                    <span>Mint as shared (unbind from active org)</span>
                  </label>
                )}
              </div>

              {/* Auto-approve — super-admin only. Devices bootstrapped with
                  this token skip the portal Approve step entirely, so it's
                  gated to bounded tokens: a leaked unlimited auto-approve
                  token would let anyone enroll trusted hardware. */}
              {isSuperAdmin && (
                <div className="rounded-md border border-status-warning/30 bg-status-warning-bg/30 p-3 text-xs">
                  <label className={`flex items-center gap-2 ${autoApproveAllowed ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                    <input
                      type="checkbox"
                      checked={effectiveAutoApprove}
                      disabled={!autoApproveAllowed}
                      onChange={e => setAutoApprove(e.target.checked)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="font-medium text-foreground">Auto-approve enrolled devices</span>
                  </label>
                  <p className="mt-1.5 text-muted-foreground">
                    {autoApproveAllowed
                      ? 'Devices provisioned with this token go live immediately — no approval step in the portal. Only hand this command to people you trust with fleet access.'
                      : 'Requires a single-use or limited-use token. An unlimited token cannot auto-approve.'}
                  </p>
                </div>
              )}

              {error && (
                <div className="rounded-md border border-status-error/30 bg-status-error-bg p-3 text-xs text-status-error">
                  {error}
                </div>
              )}

              <Button variant="default" onClick={handleGenerate} disabled={generating} className="w-full">
                {generating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><Key className="w-4 h-4 mr-1" /> Generate Token</>}
              </Button>
            </>
          ) : (
            <>
              <div className="bg-muted rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Bootstrap Command</span>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(curlCommand)}>
                    {copied ? <CheckCircle className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    <span className="ml-1 text-xs">{copied ? 'Copied!' : 'Copy'}</span>
                  </Button>
                </div>
                <div className="flex items-start gap-2">
                  <Terminal className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <code className="text-xs text-foreground break-all font-mono leading-relaxed">{curlCommand}</code>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Share this command with field technicians. They run it on any Linux machine to provision it as a SimCheck edge device.
                {usageType === 'single' && ' This token can only be used once.'}
                {usageType === 'limited' && ` This token can be used for up to ${maxUses} devices.`}
                {usageType === 'unlimited' && ` This token can be reused until it expires (${expiryDays} days).`}
                {effectiveAutoApprove && ' Devices enrolled with it are approved automatically — they will not appear in Pending Approvals.'}
              </p>

              <Button variant="outline" onClick={onClose} className="w-full">Done</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Device Auth Filter Config ─────────────────────────────────────────

const authFilterHelper = createColumnConfigHelper<DeviceAuthInfo>();

const getAuthFilterConfigs = () => [
  authFilterHelper
    .text()
    .id('name')
    .accessor((row) => row.name)
    .displayName('Name')
    .icon(Server)
    .build(),
  authFilterHelper
    .text()
    .id('deviceId')
    .accessor((row) => row.deviceId)
    .displayName('Device ID')
    .icon(Hash)
    .build(),
  authFilterHelper
    .option()
    .id('authStatus')
    .accessor((row) => {
      if (row.deviceRevoked) return 'revoked';
      return row.registrationStatus;
    })
    .displayName('Auth Status')
    .icon(Shield)
    .options([
      { label: 'Approved', value: 'approved' },
      { label: 'Pending', value: 'pending' },
      { label: 'Revoked', value: 'revoked' },
      { label: 'Rejected', value: 'rejected' },
    ])
    .build(),
  authFilterHelper
    .option()
    .id('secretStatus')
    .accessor((row) => {
      if (!row.hasSecretHash) return 'none';
      return row.deviceSecretDelivered ? 'delivered' : 'pending';
    })
    .displayName('Secret Status')
    .icon(Key)
    .options([
      { label: 'Delivered', value: 'delivered' },
      { label: 'Pending Delivery', value: 'pending' },
      { label: 'No Secret', value: 'none' },
    ])
    .build(),
];

// ── Provisioning Tokens Filter Config ────────────────────────────────

const tokenFilterHelper = createColumnConfigHelper<ProvisioningToken>();

const getTokenFilterConfigs = () => [
  tokenFilterHelper
    .text()
    .id('token')
    .accessor((row) => row.token)
    .displayName('Token')
    .icon(Key)
    .build(),
  tokenFilterHelper
    .option()
    .id('status')
    .accessor((row) => row.status)
    .displayName('Status')
    .icon(Shield)
    .options([
      { label: 'Active', value: 'active' },
      { label: 'Revoked', value: 'revoked' },
      { label: 'Exhausted', value: 'exhausted' },
      { label: 'Expired', value: 'expired' },
    ])
    .build(),
  tokenFilterHelper
    .option()
    .id('usageType')
    .accessor((row) => row.usageType)
    .displayName('Usage Type')
    .icon(Hash)
    .options([
      { label: 'Single use', value: 'single' },
      { label: 'Limited', value: 'limited' },
      { label: 'Unlimited', value: 'unlimited' },
    ])
    .build(),
];

// ── Main Page ─────────────────────────────────────────────────────────

export default function DeviceProvisioning() {
  // Route-aware mount:
  //   /edges/provisioning      → tenant view: only this org's PRIVATE
  //                              devices/tokens, no shared infra controls
  //   /admin/device-provisioning → super-admin global view: all devices,
  //                              all tokens, can revoke anything
  // Detected via current path so we can serve both mounts from one component.
  const location = useLocation();
  const isPrivateMount = location.pathname.startsWith('/edges');
  const { activeOrganizationId, activeOrganization, isSuperAdmin } = useAuth();
  const toast = useToast();
  const tz = useUserTimezone();
  const [tokens, setTokens] = useState<ProvisioningToken[]>([]);

  // Org-name lookup for the Owner column (super-admin sees devices across
  // multiple orgs; show readable names, not opaque orgIds). Tenants on
  // /edges/provisioning never see other orgs so this stays empty for them.
  const [orgNames, setOrgNames] = useState<Map<string, string>>(new Map());
  const showOwnerColumn = isPrivateMount && isSuperAdmin;
  useEffect(() => {
    if (!showOwnerColumn) return;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'organizations'));
        const m = new Map<string, string>();
        snap.docs.forEach((d) => m.set(d.id, (d.data() as any)?.name || d.id));
        setOrgNames(m);
      } catch (err) {
        console.warn('Failed to load org names for Owner column:', err);
      }
    })();
  }, [showOwnerColumn]);
  const ownerLabel = (ownerOrgId: string | null | undefined): string => {
    if (!ownerOrgId) return 'Shared';
    return orgNames.get(ownerOrgId) || ownerOrgId;
  };
  const [pendingDevices, setPendingDevices] = useState<CentralEdgeDevice[]>([]);
  const [authDevices, setAuthDevices] = useState<DeviceAuthInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'pending' | 'tokens' | 'auth'>(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return t === 'pending' || t === 'tokens' || t === 'auth' ? t : 'auth';
  });
  const navigate = useNavigate();

  // Approve state
  const [approveError, setApproveError] = useState<string | null>(null);
  const [approving, setApproving] = useState(false);
  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);

  // Reject state — mirrors approve, separate so the inline confirm
  // prompts don't collide on the same row.
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [confirmRejectId, setConfirmRejectId] = useState<string | null>(null);

  // Revoke state
  const [revokeTarget, setRevokeTarget] = useState<DeviceAuthInfo | null>(null);
  // Provisioning token waiting for confirmation in the DeleteConfirmDialog
  const [pendingTokenRevoke, setPendingTokenRevoke] = useState<ProvisioningToken | null>(null);
  const [revokeConfirmText, setRevokeConfirmText] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    // Load tokens
    try {
      const headers = await getCentralAuthHeaders();
      const tokensRes = await fetch(`${CENTRAL_API_URL}/certificates/tokens`, { headers });
      if (tokensRes.ok) {
        const tokensData = await tokensRes.json();
        // Pre-normalize fields the DataTable can sort on. The raw API
        // payload has expiresAt as a Firestore Timestamp object
        // ({_seconds, _nanoseconds}) and enrolledDevices as an array
        // — DataTable's sort only handles strings/numbers, so derive
        // scalar columns alongside.
        const enriched: ProvisioningToken[] = (tokensData.tokens || []).map((t: any) => ({
          ...t,
          devicesCount: Array.isArray(t.enrolledDevices) ? t.enrolledDevices.length : 0,
          expiresMs: parseTimestamp(t.expiresAt)?.getTime() ?? 0,
        }));
        setTokens(enriched);
      }
    } catch (e) {
      console.error('Failed to load tokens:', e);
    }

    // Load devices (for pending + auth tabs)
    try {
      const allDevices = await edgeDeviceService.getDevices(true);
      // Tenant mount: only show this org's PRIVATE devices. Shared edges
      // are visible elsewhere (Locations, Quick Tests pickers) but not
      // here — tenants don't approve/revoke hardware they don't own,
      // and showing the Revoke button on shared devices is misleading
      // even if the backend rejects with 403 (canWriteDevice gates it).
      //
      // Super-admin viewing /edges/provisioning: see everything (shared +
      // every tenant's private). They need cross-org operational
      // visibility — the strict private filter would make them have to
      // navigate to /admin/device-provisioning for every cross-org glance.
      // Tenants stay strictly scoped.
      const devices = (!isPrivateMount || isSuperAdmin)
        ? allDevices
        : allDevices.filter((d: any) => activeOrganizationId && d.ownerOrgId === activeOrganizationId);

      setPendingDevices(devices.filter(d => (d as any).registrationStatus === 'pending'));

      // Build auth info for all non-demo devices
      const authInfo: DeviceAuthInfo[] = devices
        .filter(d => !(d as any).isDemo)
        .map(d => {
          const raw = d as any;
          return {
            id: d.id,
            name: d.name || d.deviceId || d.device_id || 'Unknown',
            deviceId: d.deviceId || d.device_id || '',
            ownerOrgId: raw.ownerOrgId ?? null,
            registrationStatus: raw.registrationStatus || 'approved',
            deviceRevoked: raw.deviceRevoked || false,
            deviceSecretDelivered: raw.deviceSecretDelivered || false,
            hasSecretHash: !!raw.hasDeviceSecret,
            approvedAt: parseTimestamp(raw.approvedAt),
            revokedAt: parseTimestamp(raw.revokedAt),
            restorableUntil: parseTimestamp(raw.restorableUntil),
            lastSeen: parseTimestamp(d.lastHeartbeat || raw.lastSeen || raw.last_seen),
            status: d.status || 'offline',
            installPhase: typeof raw.installPhase === 'string' ? raw.installPhase : null,
            installPhaseStartedAt: typeof raw.installPhaseStartedAt === 'number'
              ? raw.installPhaseStartedAt
              : null,
          };
        });
      setAuthDevices(authInfo);
    } catch (e) {
      console.error('Failed to load devices:', e);
      setPendingDevices([]);
    }

    setLoading(false);
  }, [isPrivateMount, activeOrganizationId, isSuperAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while any visible device is mid-install (issue #106).
  // The list endpoint caches for 5 s server-side + 15 s client-side; a
  // 5 s poll here means the strip updates within ~20 s of a phase change,
  // which paired with the local 1 s elapsed-time ticker keeps the page
  // feeling alive throughout a slow install. Cleared once no device is
  // mid-install.
  useEffect(() => {
    const anyInstalling = authDevices.some(d => d.installPhase);
    if (!anyInstalling) return;
    const id = setInterval(() => { loadData(); }, 5000);
    return () => clearInterval(id);
  }, [authDevices, loadData]);

  const handleApprove = async (deviceId: string) => {
    setApproving(true);
    setApproveError(null);
    setConfirmApproveId(null);
    try {
      await deploymentService.approveDevice(deviceId);
      // Optimistic: drop the row from the Pending tab immediately. The
      // central-server device-list cache (5 s) + frontend cache (15 s) would
      // otherwise keep the just-rejected/approved row visible long enough
      // that the user reflexively clicks again.
      setPendingDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success('Device approved', 'Opening the device page to watch installation progress.');
      // Land on the device detail page so the user can watch the install
      // flow in context (modems, status, etc.) instead of staring at an
      // emptied Pending tab. Route depends on the mount: tenants use
      // /edges/:id, super-admin /admin/edge-devices/:id.
      const detailPath = isPrivateMount
        ? `/edges/${deviceId}`
        : `/admin/edge-devices/${deviceId}`;
      navigate(detailPath);
    } catch (e: any) {
      console.error('Failed to approve device:', e);
      setApproveError(e.message || 'Failed to approve device');
      toast.error(e.message || 'Failed to approve device');
    }
    setApproving(false);
  };

  const handleReject = async (deviceId: string) => {
    setRejecting(true);
    setRejectError(null);
    setConfirmRejectId(null);
    try {
      await deploymentService.rejectDevice(deviceId);
      setPendingDevices(prev => prev.filter(d => d.id !== deviceId));
      toast.success('Device rejected', 'The device will stop polling once the updater sees the new state.');
      await loadData();
    } catch (e: any) {
      console.error('Failed to reject device:', e);
      setRejectError(e.message || 'Failed to reject device');
      toast.error(e.message || 'Failed to reject device');
    }
    setRejecting(false);
  };

  const handleRevoke = async (device: DeviceAuthInfo) => {
    setRevoking(true);
    try {
      await edgeDeviceService.revokeDevice(device.id);
      setRevokeTarget(null);
      setRevokeConfirmText('');
      setAuthError(null);
      toast.success('Device revoked', 'You can restore it from this table for the next 30 days.');
      await loadData();
    } catch (e: any) {
      console.error('Failed to revoke device:', e);
      setAuthError(e.message || 'Failed to revoke device');
    }
    setRevoking(false);
  };

  const handleRestore = async (device: DeviceAuthInfo) => {
    setRestoringId(device.id);
    try {
      await edgeDeviceService.restoreDevice(device.id);
      setAuthError(null);
      toast.success('Device restored', `${device.name} will reconnect on its own within a minute or two.`);
      await loadData();
    } catch (e: any) {
      console.error('Failed to restore device:', e);
      setAuthError(e.message || 'Failed to restore device');
    }
    setRestoringId(null);
  };

  const handleRevokeToken = async (tokenId: string) => {
    try {
      const headers = await getCentralAuthHeaders();
      const res = await fetch(`${CENTRAL_API_URL}/certificates/tokens/${tokenId}/revoke`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'Revoked via UI' }),
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error(`Revoke failed (${res.status}): ${errBody || res.statusText}`);
      }
    } catch (e: any) {
      console.error('Failed to revoke token:', e);
      // Surface it so the user knows something went wrong instead of
      // silently re-rendering the same state.
      toast.error(e?.message || 'unknown error', 'Failed to revoke token');
    }
    await loadData();
  };

  // Auth tab stats
  const authStats = useMemo(() => ({
    total: authDevices.length,
    approved: authDevices.filter(d => d.registrationStatus === 'approved' && !d.deviceRevoked).length,
    pending: authDevices.filter(d => d.registrationStatus === 'pending').length,
    revoked: authDevices.filter(d => d.deviceRevoked).length,
    secretsActive: authDevices.filter(d => d.deviceSecretDelivered).length,
  }), [authDevices]);

  // Auth tab filters. Append an Owner filter when super-admin views
  // /edges/provisioning so they can scope the Device Auth list down to
  // a specific tenant or to shared infra.
  const authFilterColumnsConfig = useMemo(() => {
    const base = getAuthFilterConfigs();
    if (!showOwnerColumn) return base;
    return [
      ...base,
      authFilterHelper
        .option()
        .id('owner')
        .accessor((row) => (row.ownerOrgId ?? '__shared__') as string)
        .displayName('Owner')
        .icon(Server)
        .options([
          { label: 'Shared', value: '__shared__' },
          ...Array.from(orgNames.entries())
            .map(([id, name]) => ({ label: name, value: id }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ])
        .build(),
    ];
  }, [showOwnerColumn, orgNames]);
  const { columns: authFilterColumns, filters: authFilters, actions: authActions, strategy: authStrategy } = useDataTableFilters({
    strategy: 'client',
    columnsConfig: authFilterColumnsConfig,
    data: authDevices,
  });
  const filteredAuthDevices = useMemo(
    () => applyFilters(authDevices, authFilters, authFilterColumnsConfig),
    [authDevices, authFilters, authFilterColumnsConfig]
  );

  // Provisioning Tokens tab filters
  const tokenFilterColumnsConfig = useMemo(() => getTokenFilterConfigs(), []);
  const { columns: tokenFilterColumns, filters: tokenFilters, actions: tokenActions, strategy: tokenStrategy } = useDataTableFilters({
    strategy: 'client',
    columnsConfig: tokenFilterColumnsConfig,
    data: tokens,
  });
  const filteredTokens = useMemo(
    () => applyFilters(tokens, tokenFilters, tokenFilterColumnsConfig),
    [tokens, tokenFilters, tokenFilterColumnsConfig]
  );

  // Tokens DataTable columns. `key` matches a sortable scalar field on
  // the row (DataTable sorts via `(row as any)[column.key]`); the render
  // function shapes the cell visually. `useCount`, `devicesCount`, and
  // `expiresMs` are the scalars; `actions` stays unsortable.
  const tokenTableColumns: Column<ProvisioningToken>[] = [
    {
      key: 'token',
      header: 'Token',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => (
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-muted-foreground shrink-0" />
          <code className="text-xs text-muted-foreground font-mono">{row.token.substring(0, 10)}…</code>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => {
        if (row.status === 'active') return <Badge variant="secondary" className="text-xs"><ShieldCheck className="w-3 h-3 mr-1" /> Active</Badge>;
        if (row.status === 'revoked') return <Badge variant="destructive" className="text-xs"><ShieldX className="w-3 h-3 mr-1" /> Revoked</Badge>;
        if (row.status === 'exhausted') return <Badge variant="outline" className="text-xs"><ShieldAlert className="w-3 h-3 mr-1" /> Exhausted</Badge>;
        if (row.status === 'expired') return <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-1" /> Expired</Badge>;
        return <Badge variant="outline" className="text-xs">{row.status}</Badge>;
      },
    },
    {
      key: 'usageType',
      header: 'Usage',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => (
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-xs capitalize">{row.usageType}</Badge>
          {row.autoApprove && (
            <Badge
              variant="outline"
              className="text-xs text-status-warning border-status-warning/30"
              title="Devices enrolled with this token are approved automatically"
            >
              <ShieldCheck className="w-3 h-3 mr-1" /> Auto-approve
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'useCount',
      header: 'Uses',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => (
        <span className="text-xs text-muted-foreground">
          {row.useCount}{row.maxUses ? `/${row.maxUses}` : ''}
        </span>
      ),
    },
    {
      key: 'devicesCount',
      header: 'Devices',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => (
        <span className="text-xs text-muted-foreground">{row.devicesCount ?? row.enrolledDevices?.length ?? 0}</span>
      ),
    },
    {
      key: 'expiresMs',
      header: 'Expires',
      sortable: true,
      render: (_v: unknown, row: ProvisioningToken) => {
        const d = parseTimestamp(row.expiresAt);
        return <span className="text-xs text-muted-foreground">{d ? d.toLocaleDateString(undefined, { timeZone: tz }) : '—'}</span>;
      },
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (_v: unknown, row: ProvisioningToken) => {
        if (row.status !== 'active') return <span className="text-xs text-muted-foreground italic">—</span>;
        return (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setPendingTokenRevoke(row); }} className="text-destructive text-xs">
            <XCircle className="w-3 h-3 mr-1" /> Revoke
          </Button>
        );
      },
    },
  ];

  // Auth DataTable columns
  const authTableColumns: Column<DeviceAuthInfo>[] = [
    {
      key: 'name',
      header: 'Device',
      render: (_v: unknown, row: DeviceAuthInfo) => (
        <button
          onClick={() => navigate(`/admin/edge-devices/${row.id}`)}
          className="flex items-center gap-2 text-left hover:text-accent-light-blue transition-colors"
        >
          <Server className="w-4 h-4 text-muted-foreground shrink-0" />
          <div>
            <div className="font-medium text-foreground">{row.name}</div>
            <div className="text-xs text-muted-foreground font-mono">{row.deviceId}</div>
          </div>
        </button>
      ),
    },
    // Owner column — only when the page may surface devices from
    // multiple orgs (super-admin viewing /edges/provisioning). Tenants
    // only see their own org so it'd be redundant.
    ...(showOwnerColumn ? [{
      key: 'ownerOrgId',
      header: 'Owner',
      render: (_v: unknown, row: DeviceAuthInfo) => {
        if (!row.ownerOrgId) {
          return (
            <Badge variant="outline" className="text-xs bg-muted/40 text-muted-foreground border-border">
              Shared
            </Badge>
          );
        }
        const isMine = row.ownerOrgId === activeOrganizationId;
        return (
          <Badge
            variant="outline"
            className={isMine
              ? 'text-xs bg-accent-blue/10 text-accent-light-blue border-accent-blue/30'
              : 'text-xs'}
          >
            {ownerLabel(row.ownerOrgId)}{isMine ? ' (yours)' : ''}
          </Badge>
        );
      },
    }] : []),
    {
      key: 'authStatus',
      header: 'Auth Status',
      render: (_v: unknown, row: DeviceAuthInfo) => {
        if (row.deviceRevoked) return <Badge variant="destructive" className="text-xs"><ShieldX className="w-3 h-3 mr-1" /> Revoked</Badge>;
        if (row.registrationStatus === 'pending') return <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/30"><ShieldAlert className="w-3 h-3 mr-1" /> Pending</Badge>;
        if (row.registrationStatus === 'approved') {
          // Mid-install — show live phase strip instead of the static
          // Approved badge. The admin is watching the post-approval install
          // flow here; phase + elapsed time is the answer to "is it stuck?"
          if (row.installPhase) {
            return (
              <InstallProgressBadge
                phase={row.installPhase}
                startedAt={row.installPhaseStartedAt ?? undefined}
              />
            );
          }
          return <Badge variant="secondary" className="text-xs"><ShieldCheck className="w-3 h-3 mr-1" /> Approved</Badge>;
        }
        return <Badge variant="outline" className="text-xs">{row.registrationStatus}</Badge>;
      },
    },
    {
      key: 'secretStatus',
      header: 'Device Secret',
      render: (_v: unknown, row: DeviceAuthInfo) => {
        if (!row.hasSecretHash) return <span className="text-xs text-muted-foreground">—</span>;
        if (row.deviceSecretDelivered) return (
          <div className="flex items-center gap-1 text-xs">
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
            <span className="text-emerald-500">Active</span>
          </div>
        );
        return (
          <div className="flex items-center gap-1 text-xs">
            <Clock className="w-3.5 h-3.5 text-yellow-500" />
            <span className="text-yellow-500">Pending delivery</span>
          </div>
        );
      },
    },
    {
      key: 'connection',
      header: 'Connection',
      render: (_v: unknown, row: DeviceAuthInfo) => {
        const isOnline = row.status === 'online';
        return (
          <div className="flex items-center gap-1.5 text-xs">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
            <span className={isOnline ? 'text-emerald-500' : 'text-muted-foreground'}>{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        );
      },
    },
    {
      key: 'approvedAt',
      header: 'Approved',
      render: (_v: unknown, row: DeviceAuthInfo) => <span className="text-xs text-muted-foreground">{formatDate(row.approvedAt, tz)}</span>,
    },
    {
      key: 'lastSeen',
      header: 'Last Seen',
      render: (_v: unknown, row: DeviceAuthInfo) => <span className="text-xs text-muted-foreground">{formatRelative(row.lastSeen)}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (_v: unknown, row: DeviceAuthInfo) => {
        if (row.deviceRevoked) {
          const restorable = !!row.restorableUntil && row.restorableUntil.getTime() > Date.now();
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="text-xs text-muted-foreground italic">
                Revoked {formatDate(row.revokedAt, tz)}
                {!restorable && ' · re-install required'}
              </span>
              {restorable && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs"
                  disabled={restoringId === row.id}
                  title={`Restorable until ${formatDate(row.restorableUntil, tz)}`}
                  onClick={(e) => { e.stopPropagation(); handleRestore(row); }}
                >
                  {restoringId === row.id
                    ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    : <RotateCcw className="w-3 h-3 mr-1" />}
                  Restore
                </Button>
              )}
            </div>
          );
        }
        if (row.registrationStatus === 'approved') return (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setRevokeTarget(row); }} className="text-destructive text-xs">
            <XCircle className="w-3 h-3 mr-1" /> Revoke
          </Button>
        );
        return null;
      },
    },
  ];

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isPrivateMount ? 'My Device Provisioning' : 'Device Provisioning'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {!isPrivateMount
                ? 'Provision new devices, manage tokens, and monitor device authentication across the platform'
                : isSuperAdmin
                  ? `Devices visible to ${activeOrganization?.name || 'your active organization'}, plus shared and other tenants' devices (filter by Owner to scope the view)`
                  : `Provision new devices for ${activeOrganization?.name || 'your organization'}, manage tokens, and monitor device authentication`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setLoading(true); loadData(); }}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="default" size="sm" onClick={() => setShowGenerateModal(true)}>
              <Plus className="w-4 h-4 mr-1" /> Generate Token
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('auth')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'auth' ? 'border-accent-blue text-accent-light-blue' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Shield className="w-4 h-4 inline mr-1" /> Authorized Devices
              {authStats.revoked > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">{authStats.revoked}</Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'pending' ? 'border-accent-blue text-accent-light-blue' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Server className="w-4 h-4 inline mr-1" /> Pending Devices
              {pendingDevices.length > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">{pendingDevices.length}</Badge>
              )}
            </button>
            <button
              onClick={() => setActiveTab('tokens')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tokens' ? 'border-accent-blue text-accent-light-blue' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Key className="w-4 h-4 inline mr-1" /> Provisioning Tokens
            </button>
          </nav>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading...
          </div>
        ) : (
          <>
            {/* ── Pending Devices Tab ──────────────────────────────── */}
            {activeTab === 'pending' && (
              <div className="space-y-3">
                {pendingDevices.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pending devices.</p>
                    <p className="text-xs mt-1">New devices will appear here after running the bootstrap script.</p>
                  </div>
                ) : (
                  pendingDevices.map(device => (
                    <div key={device.id} className="border border-border rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <Server className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-foreground">{device.name || device.deviceId}</span>
                          <Badge variant="outline" className="text-xs text-yellow-500 border-yellow-500/30">Pending Approval</Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                          <span>ID: <code>{device.deviceId || device.device_id}</code></span>
                          {(device as any).hostname && <span>Host: {(device as any).hostname}</span>}
                          {(device as any).platform && <span>Platform: {(device as any).platform}</span>}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {confirmApproveId === device.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Grant access?</span>
                            <Button variant="destructive" size="sm" onClick={() => handleApprove(device.id)} disabled={approving}>
                              {approving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Approving...</> : 'Yes, Approve'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmApproveId(null)}>
                              Cancel
                            </Button>
                          </div>
                        ) : confirmRejectId === device.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Reject this device?</span>
                            <Button variant="destructive" size="sm" onClick={() => handleReject(device.id)} disabled={rejecting}>
                              {rejecting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Rejecting...</> : 'Yes, Reject'}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setConfirmRejectId(null)}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setConfirmRejectId(device.id)} className="text-destructive">
                              <XCircle className="w-3 h-3 mr-1" /> Reject
                            </Button>
                            <Button variant="default" size="sm" onClick={() => setConfirmApproveId(device.id)}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Approve
                            </Button>
                          </div>
                        )}
                        {approveError && <span className="text-xs text-destructive">{approveError}</span>}
                        {rejectError && <span className="text-xs text-destructive">{rejectError}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── Provisioning Tokens Tab ──────────────────────────── */}
            {activeTab === 'tokens' && (
              <div className="space-y-4">
                {/* Filter */}
                <Card className="p-4">
                  <DataTableFilter
                    columns={tokenFilterColumns}
                    filters={tokenFilters}
                    actions={tokenActions}
                    strategy={tokenStrategy}
                  />
                </Card>

                {/* Table */}
                <DataTable
                  data={filteredTokens}
                  columns={tokenTableColumns}
                  keyExtractor={(row) => row.id}
                  loading={false}
                  emptyMessage="No provisioning tokens yet. Generate a token to start provisioning edge devices."
                />
              </div>
            )}

            {/* ── Device Auth Tab ──────────────────────────────────── */}
            {activeTab === 'auth' && (
              <div className="space-y-4">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Total Devices', value: authStats.total, icon: Server, color: 'text-foreground' },
                    { label: 'Approved', value: authStats.approved, icon: ShieldCheck, color: 'text-emerald-500' },
                    { label: 'Pending', value: authStats.pending, icon: ShieldAlert, color: 'text-yellow-500' },
                    { label: 'Revoked', value: authStats.revoked, icon: ShieldX, color: 'text-destructive' },
                    { label: 'Secrets Active', value: authStats.secretsActive, icon: Key, color: 'text-accent-light-blue' },
                  ].map(s => (
                    <Card key={s.label} className="p-4">
                      <div className="flex items-center gap-2">
                        <s.icon className={`w-4 h-4 ${s.color}`} />
                        <span className="text-xs text-muted-foreground">{s.label}</span>
                      </div>
                      <div className={`text-2xl font-bold mt-1 ${s.color}`}>{s.value}</div>
                    </Card>
                  ))}
                </div>

                {authError && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {authError}
                  </div>
                )}

                {/* Filter */}
                <Card className="p-4">
                  <DataTableFilter
                    columns={authFilterColumns}
                    filters={authFilters}
                    actions={authActions}
                    strategy={authStrategy}
                  />
                </Card>

                {/* Table */}
                <DataTable
                  data={filteredAuthDevices}
                  columns={authTableColumns}
                  keyExtractor={(row) => row.id}
                  loading={false}
                  emptyMessage="No devices found"
                />
              </div>
            )}
          </>
        )}

        {showGenerateModal && (
          <GenerateTokenModal
            onClose={() => setShowGenerateModal(false)}
            onGenerated={loadData}
          />
        )}

        <DeleteConfirmDialog
          open={pendingTokenRevoke !== null}
          onClose={() => setPendingTokenRevoke(null)}
          title="Revoke token?"
          verb="revoke"
          confirmText="Revoke"
          itemName={pendingTokenRevoke ? `${pendingTokenRevoke.token.substring(0, 10)}…` : undefined}
          details="New devices can no longer enroll with it."
          onConfirm={async () => {
            if (pendingTokenRevoke) await handleRevokeToken(pendingTokenRevoke.id);
          }}
        />

        {/* Revoke Confirmation Modal */}
        {revokeTarget && (
          <Modal
            isOpen={true}
            onClose={() => { setRevokeTarget(null); setRevokeConfirmText(''); }}
            title="Revoke Device Access"
          >
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Revoke access for <strong className="text-foreground">{revokeTarget.name}</strong>
                {' '}(<code className="font-mono text-xs">{revokeTarget.deviceId}</code>)?
              </p>
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive space-y-1">
                <p>
                  <strong>Warning:</strong> The device disconnects immediately and stops running tests.
                </p>
                <p>
                  You can restore it from this table for the next 30 days. After that, getting it back
                  means re-installing it on site with a new provisioning token.
                </p>
                {revokeTarget.status === 'online' && (
                  <p><strong>This device is online right now.</strong></p>
                )}
              </div>
              <div className="space-y-1.5">
                <label htmlFor="revoke-confirm" className="text-xs text-muted-foreground">
                  Type <strong className="text-foreground">{revokeTarget.name}</strong> to confirm
                </label>
                <Input
                  id="revoke-confirm"
                  value={revokeConfirmText}
                  onChange={(e) => setRevokeConfirmText(e.target.value)}
                  autoComplete="off"
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setRevokeTarget(null); setRevokeConfirmText(''); }} disabled={revoking}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleRevoke(revokeTarget)}
                  disabled={revoking || revokeConfirmText.trim() !== revokeTarget.name}
                >
                  {revoking ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Revoking...</> : <><ShieldX className="w-4 h-4 mr-1" /> Revoke Access</>}
                </Button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    </AppShell>
  );
}
